import { AlertCircle } from "lucide-react";
import { useMemo } from "react";
import { Link } from "react-router-dom";
import { z } from "zod";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaForm } from "@/shared/components/forms/ColocaForm";
import { ColocaInputField } from "@/shared/components/forms/ColocaInputField";
import { ColocaPasswordField } from "@/shared/components/forms/ColocaPasswordField";
import { useZodForm } from "@/shared/components/forms/useZodForm";
import useTranslations from "@/shared/lang/useTranslations";

interface LoginFormProps {
  isLoading: boolean;
  doLogin: (values: { email: string; password: string }) => void;
  error?: string;
}

export const LoginForm = ({ isLoading, doLogin, error }: LoginFormProps) => {
  const { t, activeLanguage } = useTranslations("login");
  const { t: tg } = useTranslations("global");
  const { t: tr } = useTranslations("register");

  const schema = useMemo(
    () =>
      z.object({
        email: z.string().min(1, tg("input_error_required")).email(tg("input_error_invalid_email")),
        password: z.string().min(6, tg("input_error_min_length", { length: 6 })),
      }),
    [tg],
  );

  const form = useZodForm(schema, { email: "", password: "" }, "onChange");
  const {
    formState: { isValid },
  } = form;

  return (
    <ColocaForm
      key={activeLanguage}
      form={form}
      onSubmit={(values) => doLogin({ email: values.email, password: values.password })}
      className="w-full max-w-xl space-y-4 animate-fade-up"
    >
      <ColocaInputField
        name="email"
        type="email"
        label={t("input_user_label")}
        placeholder={t("input_user_placeholder")}
      />
      <ColocaPasswordField
        name="password"
        label={t("input_password_label")}
        placeholder={t("input_password_placeholder")}
        autoComplete="current-password"
        showLabel={tg("input_show_password_label")}
        hideLabel={tg("input_hide_password_label")}
      />

      <div className="flex justify-end -mt-2">
        <Link
          to="/auth/forgot-password"
          className="text-xs font-semibold text-primary transition-colors hover:underline"
        >
          {t("forgot_password_link")}
        </Link>
      </div>

      {error && (
        <div className="animate-shake">
          <ColocaAlert variant="destructive" icon={AlertCircle} text={error} />
        </div>
      )}

      <ColocaButton
        type="submit"
        variant="primary"
        size="lg"
        loading={isLoading}
        disabled={!isValid || isLoading}
        className="h-12 w-full text-base font-semibold"
      >
        {t("login_button")}
      </ColocaButton>

      <p className="text-center text-sm text-muted-foreground">
        {tr("create_account_label_prefix")}{" "}
        <Link to="/auth/register" className="font-semibold text-primary transition-colors hover:underline">
          {tr("create_account_label_button")}
        </Link>
      </p>
    </ColocaForm>
  );
};

export default LoginForm;
